import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useSupportMessages,
  useSendSupportMessage,
  useMarkThreadRead,
} from "@/hooks/useSupportThreads";
import { useAuth } from "@/lib/auth";
import { format } from "date-fns";
import { ArrowLeft, Send, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ThreadViewProps {
  threadId: string;
  onBack: () => void;
  senderRole: "user" | "admin";
}

export function ThreadView({ threadId, onBack, senderRole }: ThreadViewProps) {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: messages, isLoading } = useSupportMessages(threadId);
  const sendMessage = useSendSupportMessage();
  const markRead = useMarkThreadRead();

  // Mark thread as read when opened
  useEffect(() => {
    if (threadId) {
      markRead.mutate(threadId);
    }
  }, [threadId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!message.trim()) return;

    try {
      await sendMessage.mutateAsync({
        threadId,
        body: message.trim(),
        senderRole,
      });
      setMessage("");
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100%-80px)]">
      <Button variant="ghost" size="sm" onClick={onBack} className="self-start mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to requests
      </Button>

      <ScrollArea className="flex-1 pr-4">
        <div className="space-y-4 pb-4">
          {messages?.map((msg) => {
            const isOwn =
              (senderRole === "user" && msg.sender_role === "user") ||
              (senderRole === "admin" && msg.sender_role === "admin" && msg.sender_id === user?.id);

            return (
              <div
                key={msg.id}
                className={cn(
                  "flex gap-3",
                  isOwn ? "flex-row-reverse" : "flex-row"
                )}
              >
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarImage src={msg.sender_avatar || undefined} />
                  <AvatarFallback className="text-xs">
                    {msg.sender_name?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>
                <div
                  className={cn(
                    "max-w-[75%] rounded-lg px-4 py-2",
                    isOwn
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium">
                      {msg.sender_name}
                    </span>
                    <span
                      className={cn(
                        "text-xs",
                        isOwn ? "text-primary-foreground/70" : "text-muted-foreground"
                      )}
                    >
                      {format(new Date(msg.created_at), "MMM d, h:mm a")}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <div className="flex gap-2 mt-4 pt-4 border-t">
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Type your message..."
          rows={2}
          className="resize-none"
        />
        <Button
          onClick={handleSend}
          disabled={!message.trim() || sendMessage.isPending}
          size="icon"
          className="h-auto"
        >
          {sendMessage.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
